import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';
import type { ZodObject, z } from 'zod';
import { useAutoSave } from './useAutoSave';

type MessageType = 'error' | 'success' | 'default';

export type UseForm<T extends ZodObject> = ReturnType<typeof useForm<T>>;

export function useForm<
  T extends ZodObject,
  U extends object = Record<keyof z.infer<T>, unknown>
>(schema: T, input: U, onInputChange?: () => Promise<void>) {
  const [error, setError] = useState<z.ZodError>();
  const [isTouched, setIsTouched] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  function issue<K extends keyof U>(
    key?: K,
    pathFromKey?: (string | number)[],
    options?: { partial?: boolean }
  ): z.ZodIssue | undefined {
    return isTouched && key
      ? error?.issues?.find((issue) =>
          options?.partial
            ? [key, ...(pathFromKey ?? [])].every((segment, index) =>
                isEqual(issue.path[index], segment)
              )
            : isEqual(issue.path, [key, ...(pathFromKey ?? [])])
        )
      : error?.issues[0];
  }

  function hasIssue<K extends keyof U>(
    key?: K,
    pathFromKey?: (string | number)[],
    options?: { partial?: boolean }
  ): boolean {
    return issue(key, pathFromKey, options) !== undefined;
  }

  function isValid(): boolean {
    return isTouched && !hasIssue();
  }

  function message<K extends keyof U>(
    key: K,
    pathFromKey?: (string | number)[],
    whenValid?: string,
    options?: { partial?: boolean }
  ): string | undefined {
    return messageType(key, pathFromKey, options) === 'success' && whenValid
      ? whenValid
      : issue(key, pathFromKey, options)?.message;
  }

  function messageType<K extends keyof U>(
    key: K,
    pathFromKey?: (string | number)[],
    options?: { partial?: boolean }
  ): MessageType {
    if (isTouched) {
      if (hasIssue(key, pathFromKey, options)) {
        return 'error';
      }
      return 'success';
    }
    return 'default';
  }

  const validate = async (
    onValid?: (validInput: z.infer<T>) => Promise<void>,
    onInvalid?: (error: z.ZodError) => void
  ) => {
    try {
      const validInput: z.infer<T> = await schema.parseAsync(input);
      setError(undefined);
      await onValid?.(validInput);
    } catch (error) {
      console.error(error, (error as z.ZodError).issues);
      setIsTouched(true);
      setError(error as z.ZodError);
      onInvalid?.(error as z.ZodError);
    }
  };

  const reset = () => {
    setTimeout(() => {
      setIsTouched(false);
      setError(undefined);
    }, 1);
  };

  const inputKey = JSON.stringify(input);

  useEffect(() => {
    (async () => {
      if (isTouched) {
        await validate();
      } else {
        if (Object.values(input).some((value) => !!value)) {
          if (hasIssue()) {
            setIsTouched(true);
            await validate();
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey]);

  const { triggerSave } = useAutoSave({
    onSave: () => onInputChange?.(),
    delay: 500
  });

  useEffect(() => {
    if (isInitialized && !hasIssue()) {
      triggerSave?.();
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey]);

  return {
    isTouched,
    isValid,
    hasIssue,
    message,
    messageType,
    reset,
    validate,
    input,
    error,
    schema
  };
}
